import { toIsoDateString } from '@/core/models/management-reporting.models';
import type { TrialBalanceOption } from '@/core/models/reports.models';

/**
 * APT376: a snapshot copies the whole trial balance as the period's balances, and a trial balance line carries no date, so a trial balance for a
 * whole year given to one month shows the full-year figures in every month. The API refuses a trial balance that is not for the period
 * (it must end on the period's last day and start on or before its first: exactly the period, or a running year-to-date one); this is the same rule,
 * so the list can put the ones that fit first and say why a choice would be refused before Save.
 *
 * True when it fits, false when it does not, null when there is nothing to compare (the period or the trial balance has no dates).
 */
export function trialBalanceFitsPeriod(
  trialBalance: Pick<TrialBalanceOption, 'fromDate' | 'toDate'>,
  periodStart: string,
  periodEnd: string,
): boolean | null {
  const from = toIsoDateString(trialBalance.fromDate);
  const to = toIsoDateString(trialBalance.toDate);
  if (!from || !to || !periodStart || !periodEnd) return null;
  return to === periodEnd && from <= periodStart;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 2025-01-31 -> 31 Jan 2025 (the ISO text is read as it is, never through a Date, so no time zone can move it). */
export function dayLabel(iso: string): string {
  const [year, month, day] = toIsoDateString(iso).split('-');
  const name = MONTHS[Number(month) - 1];
  return year && name && day ? `${day} ${name} ${year}` : iso;
}

export const FITS_GROUP = 'For this period';
export const OTHER_GROUP = 'Other dates (the snapshot would be refused)';

export interface PeriodTrialBalanceOption extends TrialBalanceOption {
  /** What the list shows: the trial balance's reference and the dates it covers. */
  display: string;
  /** Set only when the period's dates are known: the group the list puts it in. */
  group?: string;
}

/** The trial balance list for a period's dialog: each shows its dates, and those that fit the period's dates come first. */
export function trialBalanceOptionsForPeriod(
  trialBalances: TrialBalanceOption[],
  periodStart: string,
  periodEnd: string,
): PeriodTrialBalanceOption[] {
  const options = trialBalances.map((tb): PeriodTrialBalanceOption => {
    const from = toIsoDateString(tb.fromDate);
    const to = toIsoDateString(tb.toDate);
    const dates = from && to ? ` · ${dayLabel(from)} - ${dayLabel(to)}` : '';
    const fits = trialBalanceFitsPeriod(tb, periodStart, periodEnd);
    return {
      ...tb,
      display: `${tb.refNo || tb.label}${dates}`,
      group: fits === null ? undefined : fits ? FITS_GROUP : OTHER_GROUP,
    };
  });
  // The fitting ones first; the order within each is the API's (the list is already newest first).
  return [...options].sort((a, b) => Number(b.group === FITS_GROUP) - Number(a.group === FITS_GROUP));
}

/** The reason shown under the form when the chosen trial balance is not for the period's dates; empty when it is (or cannot be told). */
export function trialBalanceMismatchHint(
  trialBalance: TrialBalanceOption | undefined,
  periodName: string,
  periodStart: string,
  periodEnd: string,
): string {
  if (!trialBalance || trialBalanceFitsPeriod(trialBalance, periodStart, periodEnd) !== false) return '';
  const from = dayLabel(toIsoDateString(trialBalance.fromDate));
  const to = dayLabel(toIsoDateString(trialBalance.toDate));
  const name = periodName.trim() || 'this period';
  return (
    `${trialBalance.refNo || trialBalance.label} covers ${from} to ${to}, but ${name} runs ${dayLabel(periodStart)} to ${dayLabel(periodEnd)}. ` +
    `A snapshot copies the trial balance as it is, so choose a trial balance for those dates (or one that runs from the start of the year to ${dayLabel(periodEnd)}).`
  );
}
